import { getErrorMessage } from '@/lib/errors'
import { extractEdgeError } from '@/lib/invokeEdge'
import { supabase } from '@/lib/supabase'
import type {
  AnswerFlags,
  PerComponentFeedback,
  SessionConfig,
  StarScores,
  TranscriptTurn,
} from '@/types/interview'
import { useCallback, useRef, useState } from 'react'

export type SessionPhase = 'idle' | 'loading' | 'resuming' | 'questioning' | 'complete' | 'error'

interface CurrentQuestion {
  id: string
  text: string
}

interface LastAnswer {
  scores: StarScores
  feedback: PerComponentFeedback
  flags: AnswerFlags
}

interface SessionState {
  phase: SessionPhase
  sessionId: string | null
  // Only populated by resumeSession — startSession/submitAnswer sessions are
  // always in_progress by construction. Lets the page distinguish a session
  // that finished normally from one the abandoned-session sweep expired, so
  // it doesn't show a "Continue to Results" CTA for a session with no report.
  sessionStatus: 'in_progress' | 'completed' | 'abandoned' | null
  currentQuestion: CurrentQuestion | null
  isCurrentQuestionFollowUp: boolean
  questionIndex: number
  totalQuestions: number
  probeCount: number
  lastAnswer: LastAnswer | null
  error: string | null
}

const INITIAL_STATE: SessionState = {
  phase: 'idle',
  sessionId: null,
  sessionStatus: null,
  currentQuestion: null,
  isCurrentQuestionFollowUp: false,
  questionIndex: 0,
  totalQuestions: 0,
  probeCount: 0,
  lastAnswer: null,
  error: null,
}

interface StartResponse {
  sessionId: string
  questionId: string
  questionText: string
  competency: string
  totalQuestions: number
  questionIndex: number
}

interface AnswerResponse {
  scores: StarScores
  feedback: PerComponentFeedback
  flags: AnswerFlags
  followUp: { questionId: string; questionText: string } | null
  nextQuestion: { questionId: string; questionText: string; competency: string } | null
}

interface ResumeResponse {
  sessionId: string
  status: 'in_progress' | 'completed' | 'abandoned'
  totalQuestions: number
  questionIndex: number
  probeCount: number
  turns: TranscriptTurn[]
  currentQuestion: { questionId: string; questionText: string } | null
  isCurrentQuestionFollowUp: boolean
}

export function useSession() {
  const [state, setState] = useState<SessionState>(INITIAL_STATE)
  // Latest state for callbacks — submitAnswer previously read a stale closure snapshot
  const stateRef = useRef(state)
  stateRef.current = state
  // Blocks concurrent submissions (e.g. ⌘↵ double-fire before the loading state flushes)
  const inFlightRef = useRef(false)

  const startSession = useCallback(async (config: SessionConfig) => {
    setState({ ...INITIAL_STATE, phase: 'loading' })

    const { data, error } = await supabase.functions.invoke<StartResponse>('interview-engine', {
      body: {
        action: 'start',
        targetRole: config.targetRole,
        difficulty: config.difficulty,
        ...(config.jobDescription ? { jobDescription: config.jobDescription } : {}),
      },
    })

    if (error || !data) {
      const message = await resolveError(error)
      setState((s) => ({ ...s, phase: 'error', error: message }))
      return
    }

    setState({
      phase: 'questioning',
      sessionId: data.sessionId,
      sessionStatus: 'in_progress',
      currentQuestion: { id: data.questionId, text: data.questionText },
      isCurrentQuestionFollowUp: false,
      questionIndex: data.questionIndex,
      totalQuestions: data.totalQuestions,
      probeCount: 0,
      lastAnswer: null,
      error: null,
    })
  }, [])

  // Returns false when the submission failed, so the caller can roll back its
  // optimistic transcript write and keep the user's draft for a retry.
  const submitAnswer = useCallback(async (answerText: string): Promise<boolean> => {
    const snapshot = stateRef.current
    if (inFlightRef.current) return false
    if (!snapshot.sessionId || !snapshot.currentQuestion) return false
    inFlightRef.current = true

    setState((s) => ({ ...s, phase: 'loading', error: null }))

    try {
      const { data, error } = await supabase.functions.invoke<AnswerResponse>('interview-engine', {
        body: {
          action: 'answer',
          sessionId: snapshot.sessionId,
          questionId: snapshot.currentQuestion.id,
          answerText,
          probeCount: snapshot.probeCount,
        },
      })

      if (error || !data) {
        // Stay in 'questioning', not 'error': nothing transitions back out of
        // 'error', which left the composer permanently disabled with no way to
        // resend. The question is still live server-side, so the retry is valid.
        const message = await resolveError(error)
        setState((s) => ({ ...s, phase: 'questioning', error: message }))
        return false
      }

      const lastAnswer: LastAnswer = {
        scores: data.scores,
        feedback: data.feedback,
        flags: data.flags,
      }

      const followUp = data.followUp
      const nextQuestion = data.nextQuestion

      if (followUp) {
        // Probe within the same question cycle — increment probeCount
        setState((s) => ({
          ...s,
          phase: 'questioning',
          currentQuestion: { id: followUp.questionId, text: followUp.questionText },
          isCurrentQuestionFollowUp: true,
          probeCount: s.probeCount + 1,
          lastAnswer,
        }))
      } else if (nextQuestion) {
        // Advance to next main question in the set — reset probeCount
        setState((s) => ({
          ...s,
          phase: 'questioning',
          currentQuestion: {
            id: nextQuestion.questionId,
            text: nextQuestion.questionText,
          },
          isCurrentQuestionFollowUp: false,
          questionIndex: s.questionIndex + 1,
          probeCount: 0,
          lastAnswer,
        }))
      } else {
        setState((s) => ({
          ...s,
          phase: 'complete',
          sessionStatus: 'completed',
          currentQuestion: null,
          lastAnswer,
        }))
      }

      return true
    } finally {
      inFlightRef.current = false
    }
  }, [])

  // Rebuild full session state from the server — survives page refresh and
  // dashboard "continue" without relying on router state. Returns the answered
  // transcript so the page can restore the conversation, or null on failure.
  const resumeSession = useCallback(async (sessionId: string): Promise<TranscriptTurn[] | null> => {
    setState({ ...INITIAL_STATE, phase: 'resuming' })

    const { data, error } = await supabase.functions.invoke<ResumeResponse>('interview-engine', {
      body: { action: 'resume', sessionId },
    })

    if (error || !data) {
      const message = await resolveError(error)
      setState((s) => ({ ...s, phase: 'error', error: message }))
      return null
    }

    const isComplete = data.status === 'completed' || !data.currentQuestion

    setState({
      phase: isComplete ? 'complete' : 'questioning',
      sessionId: data.sessionId,
      sessionStatus: data.status,
      currentQuestion: data.currentQuestion
        ? { id: data.currentQuestion.questionId, text: data.currentQuestion.questionText }
        : null,
      isCurrentQuestionFollowUp: data.isCurrentQuestionFollowUp,
      questionIndex: data.questionIndex,
      totalQuestions: data.totalQuestions,
      probeCount: data.probeCount,
      lastAnswer: null,
      error: null,
    })

    return data.turns
  }, [])

  const reset = useCallback(() => {
    setState(INITIAL_STATE)
  }, [])

  return {
    ...state,
    startSession,
    submitAnswer,
    resumeSession,
    reset,
  }
}

async function resolveError(err: unknown): Promise<string> {
  const { code, message } = await extractEdgeError(err)
  return getErrorMessage(code, message)
}
